<template>
  <div class="c-seats-occupied">
    <div class="c-found-your-team">
      <h1>We found your team!</h1>
      <p class="overview">
        You’re almost in! This workspace is full, but you can request a seat
        from your admins to join.
      </p>
      <BittsCard class="p-24" :no-padding="false">
        <div class="flex justify-between">
          <div class="flex gap-12">
            <img
              v-if="logo"
              :src="logo"
              :alt="companyName"
              class="c-company-logo__img"
            />
            <div>
              <div class="company-name">
                {{ companyName }}
              </div>
              <div class="company-domain">
                {{ clearbitDomain }}
              </div>
            </div>
          </div>
          <BittsTag color="info"> Recommended </BittsTag>
        </div>
        <BittsTextArea
          v-model="message"
          data-testid="seat-request-message-input"
          :form-label
          class="my-24"
          :status="messageTooLong ? 'danger' : 'default'"
          danger-text="Your message may not exceed 200 characters"
          placeholder="Add a message to your request (you'll be more likely to be added)"
        />
        <BittsButton
          data-testid="request-to-join-button"
          class="w-full"
          :loading="sendingRequest"
          :disabled="messageTooLong || sendingRequest"
          text="Request to join"
          @click="handleSendSeatRequest"
        />
      </BittsCard>
      <p class="not-your-team">
        Not your team?
        <BittsLink
          class="action-button"
          url="#"
          :open-in-new-tab="true"
          text="contact our support team"
          @click="contactSupport"
        />
      </p>
      <BittsButton
        class="flex sm:hidden w-full"
        variant="outline"
        type="neutral"
        text="Log out"
        :right-icon="['fak', 'logout']"
        @click="handleLogout"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import {
  BittsButton,
  BittsCard,
  BittsLink,
  BittsTag,
  BittsTextArea,
} from '@crossbeam/bitts';
import { EVENT_SITES } from '@crossbeam/itly';

import { useHead } from '@unhead/vue';
import { computed, ref, toRefs } from 'vue';
import { useRouter } from 'vue-router';

import { crossbeamApi } from '@/api';
import useIteratively from '@/composables/useIteratively';
import { CORE } from '@/constants/team';
import { SALES } from '@/constants/team_v4';
import { captureException } from '@/errors';
import { useFlashesStore, useRolesStore } from '@/stores';
import { useRootStore } from '@/stores/RootStore';
import { ClearbitCompany, Invite } from '@/types/invite';
import { intercomOrEmailMessage } from '@/utils';

const { clearbitCompany, invite, inviteCode } = defineProps<{
  clearbitCompany: ClearbitCompany | null;
  invite: Invite;
  inviteCode: string;
}>();

useHead({ title: 'Join - Crossbeam' });

const {
  name: companyName,
  logo,
  domain: clearbitDomain,
} = toRefs(clearbitCompany || { name: '', logo: null, domain: '' });

const router = useRouter();
const flashesStore = useFlashesStore();
const rootStore = useRootStore();
const { iteratively } = useIteratively();

const message = ref('');
const formLabel = computed(() => ({
  title: 'Message to Admins',
  secondaryText: `${message.value.length}/200 characters`,
}));
const messageTooLong = computed(() => message.value.length > 200);

function contactSupport() {
  intercomOrEmailMessage(
    'support@crossbeam.com',
    'Registration Support',
    `Hello, I've been recommended to join ${companyName.value}, but this is not my company`,
  );
}

const rolesStore = useRolesStore();

const seatType = computed<typeof CORE | typeof SALES>(() => {
  if (!invite.user.role_id) return CORE;
  const role = rolesStore.getRoleById(invite.user.role_id);
  if (role?.seat_type === SALES) {
    return SALES;
  }
  return CORE;
});

const sendingRequest = ref(false);
async function handleSendSeatRequest() {
  sendingRequest.value = true;
  const { data, error } = await crossbeamApi.POST('/v0.1/seat-requests', {
    body: {
      type: seatType.value,
      message: message.value,
      invite_code: inviteCode,
    },
  });
  if (error) {
    flashesStore.addErrorFlash({
      message: 'Something went wrong creating your seat request',
    });
    captureException(error);
    sendingRequest.value = false;
    return;
  }
  await rootStore.loadUserProfile();
  trackSeatRequest(data?.organization_id.toString());
  const { error: notifyError } = await crossbeamApi.POST(
    '/v0.1/invite-requests/{id}/notify',
    {
      params: { path: { id: data?.id } },
    },
  );
  if (notifyError) {
    flashesStore.addErrorFlash({
      message: 'Something went wrong creating your seat request',
    });
    captureException(notifyError);
    sendingRequest.value = false;
    return;
  }
  if (data?.id) {
    await router.push({
      name: 'invite-requests',
      params: { invite_request_uuid: data.id },
    });
  }
  sendingRequest.value = false;
}

async function handleLogout() {
  await router.push({ name: 'logout' });
}

function trackSeatRequest(requestedOrganizationId: string) {
  iteratively.userRequestedSeat({
    event_site: EVENT_SITES.FOUND_YOUR_TEAM,
    source: 'Seat Invite',
    metadata: {
      message: message.value,
      company_name: companyName.value,
      company_domain: clearbitDomain.value,
      requested_organization_id: requestedOrganizationId,
    },
  });
}
</script>
<style lang="pcss" scoped>
@reference "../../css/theme.css";
.c-seats-occupied {
  @apply w-full h-full flex items-center justify-center;

  .c-found-your-team {
    @apply max-w-[600px] mx-16;
    h1 {
      @apply text-neutral-text-strong text-xl text-center mb-16 font-bold;
    }

    p.overview {
      @apply text-neutral-text-weak text-m text-center mb-40;
    }

    .c-company-logo__img {
      @apply mb-16 h-40 w-40 rounded-8 border-neutral-border border;
    }

    .company-name {
      @apply text-neutral-text-strong text-m font-bold;
    }

    .company-domain {
      @apply text-neutral-text text-base;
    }

    .not-your-team {
      @apply mt-24 mb-40 sm:mb-0 sm:mt-40 text-neutral-text-weak text-center;
      .action-button {
        @apply text-secondary-text;
        &:hover {
          @apply opacity-80;
        }
      }
    }
  }
}
</style>
